/**
 * <TutorAvailabilityPage> — « Ma dispo » : la semaine-type du masteur (page).
 *
 * Legacy `MaDispoScreen` (route `tutor/ma-dispo`) : le bandeau de fraîcheur, l'éditeur (① la date
 * de démarrage, ② la semaine, le total), le lien « Je ne peux plus assurer un de mes créneaux »
 * quand une heure porte un live, et le bouton du bas, l'UNIQUE enregistrement.
 *
 * - **Le haut de page tient en une phrase** (Wajih, 29/10 : « trop de texte, en particulier au tout
 *   début en haut ») : l'en-tête nomme la page, le bandeau porte le seul message qui compte — une
 *   semaine se confirme chaque semaine, sinon les propositions s'arrêtent. Le titre « Ajouter mes
 *   créneaux disponibles » et sa phrase d'intro du legacy sont retirés.
 * - **Le bandeau dit, il n'agit pas** (`<AlertCard>`, une phrase sans titre) : « confirmés » en
 *   vert, « à confirmer » en information (une hygiène de la semaine, pas une faute), « ajoute tes
 *   créneaux » en alerte, le seul état qui coûte quelque chose. Il lit la semaine ENREGISTRÉE : un
 *   brouillon le repasse à « à confirmer » (le view-model le dit).
 * - **Le bouton du bas a trois mots** (`cta`) : « Tableau de bord » une fois confirmée, « Ajoute un
 *   créneau » quand rien n'est posé ni touché, « Confirmer ma dispo » sinon ; inerte tant qu'il n'y a
 *   rien à enregistrer (`canSave`).
 * - **Une relecture ne montre rien** : la roue que le legacy posait à côté du titre est retirée (le
 *   squelette du premier chargement, rien ensuite).
 *
 * Contrôlée : la semaine (brouillon compris), l'envoi et la boîte ouverte arrivent en props. La
 * page pose la boîte qu'on lui donne (`dialog`), elle ne décide pas laquelle.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { AlertCard, type AlertCardSeverity } from "../../molecules/AlertCard"
import { Header } from "../../molecules/Header"
import { TutorWeekEditor } from "../../organisms/TutorWeekEditor"
import { PageTemplate } from "../../templates/PageTemplate"
import { TutorAvailabilityDialog, type TutorAvailabilityDialogProps } from "../../templates/TutorAvailabilityDialog"
import { TutorAvailabilitySkeleton } from "./TutorAvailabilitySkeleton"
import {
    type UITutorHour,
    type UITutorWeek,
    type UITutorWeekCta,
    type UITutorWeekFreshness,
} from "../../../ports/primary/ui/types/tutorDispoOffres"

/**
 * Une page de formulaire se lit sur une colonne : la grille tient ses six colonnes de jours à
 * partir de 600 px, et au-delà de 760 une ligne de consigne devient trop longue.
 */
export const TUTOR_FORM_WIDTH = 760

const TEXTS = {
    header: "Ma dispo",
    back: "Retour",
    release: "Je ne peux plus assurer un de mes créneaux",
}

/** Le bandeau, gardé en données pour se relire d'un coup d'œil : une phrase par état, rien d'autre. */
const FRESHNESS: Record<UITutorWeekFreshness, { severity: AlertCardSeverity; message: string }> = {
    verified: { severity: "success", message: "Tes créneaux sont confirmés pour cette semaine." },
    toConfirm: {
        severity: "info",
        message: "Confirme tes créneaux chaque semaine pour continuer à recevoir des propositions.",
    },
    needsSlots: { severity: "warning", message: "Ajoute tes créneaux pour recevoir des propositions." },
}

const CTA_LABEL: Record<UITutorWeekCta, string> = {
    dashboard: "Tableau de bord",
    add: "Ajoute un créneau",
    confirm: "Confirmer ma dispo",
}

export type TutorAvailabilityPageProps = {
    /** `null` tant que la semaine n'est pas là : rien de vrai à montrer avant. */
    week: UITutorWeek | null
    freshness: UITutorWeekFreshness
    cta: UITutorWeekCta
    /** Il y a quelque chose à enregistrer : une semaine déclarée, ou un brouillon (même vidé). */
    canSave: boolean
    saving?: boolean
    startingDateOpensOn?: string
    onBack: () => void
    onPickStartingDate: (date: string) => void
    onPressHour: (hour: UITutorHour) => void
    /** « Je ne peux plus assurer un de mes créneaux » → « Libérer des créneaux ». */
    onRelease: () => void
    /** Le bouton du bas, quel que soit son mot. */
    onConfirm: () => void
    /** La boîte ouverte, s'il y en a une. */
    dialog?: TutorAvailabilityDialogProps | null
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function TutorAvailabilityPage(props: TutorAvailabilityPageProps) {
    const {
        week,
        freshness,
        cta,
        canSave,
        saving = false,
        startingDateOpensOn,
        onBack,
        onPickStartingDate,
        onPressHour,
        onRelease,
        onConfirm,
        dialog,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    const banner = FRESHNESS[freshness]
    // Le lien ne vaut que pour une heure qui porte encore un live : une heure libérée l'est déjà.
    const hasLive = week?.days.some((day) => day.slots.some((hour) => hour.state === "live")) ?? false

    const header = (
        <Header
            title={TEXTS.header}
            left={
                <IconButton
                    iconName="arrow-left"
                    appearance="transparent"
                    iconColor="primary"
                    onPress={onBack}
                    accessibilityLabel={TEXTS.back}
                />
            }
        />
    )

    const footer =
        week != null ? (
            <Button
                label={CTA_LABEL[cta]}
                iconName={cta === "dashboard" ? "check" : undefined}
                onPress={onConfirm}
                disabled={!canSave}
                loading={saving}
                fullWidth
            />
        ) : undefined

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<TutorAvailabilitySkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={TUTOR_FORM_WIDTH}
            footer={footer}
        >
            {week != null && (
                <Stack gap="6">
                    <AlertCard severity={banner.severity} message={banner.message} />
                    <TutorWeekEditor
                        week={week}
                        startingDateOpensOn={startingDateOpensOn}
                        onPickStartingDate={onPickStartingDate}
                        onPressHour={onPressHour}
                    />
                    {hasLive && (
                        <Row justify="center">
                            <TextButton label={TEXTS.release} onPress={onRelease} />
                        </Row>
                    )}
                </Stack>
            )}
            {dialog != null && <TutorAvailabilityDialog {...dialog} />}
        </PageTemplate>
    )
}
